<template>
  <div class="doc-details">
    <div v-if="documentDetails && !isParticipantSignatureProcessing && !isSiteSignatureProcessing" class="document-detail-wrap">
      <div class="row">
        <div v-if="documentDetails.docName" class="col-md-3">
          <div class="heading">Document Name</div>
          <div class="document-name">{{ documentDetails.docName }}</div>
        </div>
        <div class="col-md-3">
          <div class="heading">Participant Signed On</div>
          <div v-if="documentDetails.parentDocId">
            <div v-if="documentDetails.participantSignDate">
              {{ new Date(documentDetails.participantSignDate) | dateUTC }}
            </div>
            <div v-else class="color_red">N/A</div>
          </div>
          <div v-else>
            <div v-if="documentDetails.isParticipantSigned">
              <span v-if="documentDetails.participantSignedTime">{{ new Date(documentDetails.participantSignedTime) | formatUTCDate }}</span>
            </div>
            <div v-else class="color_red">Not signed</div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="heading">Site Signed On</div>
          <div v-if="documentDetails.parentDocId">
            <div v-if="documentDetails.siteMemberSignDate">
              {{ new Date(documentDetails.siteMemberSignDate) | dateUTC }}
            </div>
            <div v-else class="color_red">N/A</div>
          </div>
          <div v-else>
            <div v-if="documentDetails.docName == 'THREAD Study Application End User License Agreement'  || (documentDetails.docName.toLowerCase().trim() == 'datavant tokenization consent' && documentDetails.siteId == null)" class="color_red">N/A</div>
            <div v-else-if="documentDetails.isPISigned && documentDetails.docName != 'THREAD Study Application End User License Agreement' ">{{ new Date(documentDetails.piSignedTime) | formatUTCDate }}</div>
            <div v-else-if="!documentDetails.isPISigned && documentDetails.docName != 'THREAD Study Application End User License Agreement'" class="color_red">Not signed</div>
          </div>
        </div>
      </div>
    </div>
    <ParticipantInfo
      ref="participantInfoRef"
      class="clear-both"
      v-if="documentDetails && !isParticipantSignatureProcessing && !isSiteSignatureProcessing"
      :participantDetails="participantDetails"
      :documentDetails="documentDetails"
      :areActionsAllowed="areActionsAllowed"
      :extraData="extraData"
      @signatureCollected="signatureCollected"
      @processSignatureData="requestConfiguredDocumentPISignature"
      @restoreFocus="restoreFocus"
      @pIDocSignatureStatus="pIDocSignatureStatus"
    />
    <div class="document-details" id="configured-document-container" v-if="!isParticipantSignatureProcessing && !isSiteSignatureProcessing">
      <div class="configured-document-section" v-if="documentDetails.configured && !documentDetailsComputed.invalidate && !isESignRequired">
        <div class="configured-document-sub-section" :class="{ 'bottom-border': showConfiguredTags }" id="configured-document" @scroll="scrollImage">
          <div class="doc-outer-page" :style="{ width: `${fixedDocumentImageWidth}px` }">
            <div class="doc-page" id="doc-page" :style="{ left: `${calculateDocPageLeft}%`, top: '5%' }">
              <div class="plot-collection" v-if="showConfiguredTags">
                <div class="field-wrapper" v-for="(field, index) in documentDetailsComputed.siteTeamConfigureList" :key="index">
                  <!-- signature & initial -->
                  <div
                    class="sign-wrapper"
                    v-bind:style="{
                      top: `${field['plottingPts'].y}px`,
                      left: `${field['plottingPts'].x}px`
                    }"
                  >
                    <div class="fieldValue" @click="selectField(index)" v-if="field.fieldType == 'signature' || field.fieldType == 'initials' || field.fieldType == 'name'" v-bind:id="`configured-field-${index}`">
                      <span class="image-cropper"><img class="field-image" :src="field.image"/></span>
                    </div>
                  </div>
                  <!-- checkbox response -->
                  <div
                    class="checkbox-wrapper"
                    v-if="field.fieldType == 'checkbox'"
                    v-bind:style="{
                      top: `${field['plottingPts'].y}px`,
                      left: `${field['plottingPts'].x}px`
                    }"
                  >
                    <span class="fieldValue" @click="selectField(index)" v-if="field.responseText === 'true'" v-bind:id="`configured-field-${index}`">
                      <span class="image-cropper"><img class="field-image checkbox-img" :src="field.image"/></span>
                    </span>
                    <span class="fieldValue" @click="selectField(index)" v-else v-bind:id="`configured-field-${index}`">
                      <span class="image-cropper"><img class="field-image checkbox-img" src="/static/img/unchecked-checkmark-icon.svg"/></span>
                    </span>
                    <span class="checkbox-label">{{ field.fieldLabel }}</span>
                  </div>
                  <!-- text response -->
                  <div
                    class="txtarea-wrapper"
                    v-if="field.fieldType == 'text'"
                    v-bind:style="{
                      top: `${field['plottingPts'].y}px`,
                      left: `${field['plottingPts'].x}px`
                    }"
                  >
                    <textarea :maxlength="textAreaCharLimit" class="txtarea" rows="2" cols="14" v-if="field.isFocused" v-model="field.responseText" @input="checkTextResponse(field)" v-bind:id="`configured-field-${index}`"></textarea>
                    <div class="fieldValue" @click="selectField(index)" v-else v-bind:id="`configured-field-${index}`">
                      <span class="image-cropper"><img class="field-image txtarea-img" :src="field.image"/></span>
                    </div>
                    <div class="text-right" v-if="field.isFocused">{{ textAreaCharCount }} of {{ textAreaCharLimit }}</div>
                  </div>
                </div>
              </div>
              <div v-if="!documentDetailsComputed.invalidate">
                <div class="doc-img" data-page="1" :style="{ width: `${fixedDocumentImageWidth}px` }" style="height: 'auto'" v-for="(documentImage, index) in documentDetailsComputed.documentImages" :key="index">
                  <img v-bind:src="`data:image/png;base64,${documentImage.image}`" :width="`${fixedDocumentImageWidth}px`" :height="`${fixedDocumentImageHeight}px`" />
                  <div class="separator" v-if="index < documentDetailsComputed.documentImages.length - 1"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="document-footer" v-if="showConfiguredTags">
          <div class="row btn-container">
            <div class="col-md-5 col-xl-5 text-left">
              <span v-if="showPreviousBtn" class="previous-btn" @click="previousField()"><i class="fa fa-angle-left fs-23 mt-5 mr-8 arrow-icon"></i><span class="previous-text">Previous Field</span></span>
            </div>
            <div class="col-md-7 col-xl-7 text-right">
              <button class="cancel-btn" @click="cancelConfiguredDocumentSigning()">
                Cancel
              </button>
              <button v-if="showSkipResponse" class="skip-btn" @click="nextField('skip')">
                {{ footerBtnLabel.skipResponse }}
              </button>
              <button
                class="save-btn"
                :class="{
                  disabled: disableMarkResponseBtn,
                  hover: !disableMarkResponseBtn
                }"
                :disabled="disableMarkResponseBtn"
                @click="nextField('mark')"
              >
                {{ footerBtnLabel.markResponse }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- Invalidated Document Starts here -->
      <iframe v-else-if="documentDetailsComputed.invalidate" :src="documentDetailsComputed.configuredDocumentS3Url" style="width:100%; height:700px;" frameborder="0"></iframe>
      <!-- Invalidated Document Ends here -->

      <iframe v-else-if="documentDetails && !documentDetails.configured && documentDetails.preSignedUrl" :src="documentDetails.preSignedUrl" style="width:100%; height:700px;" frameborder="0"></iframe>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import signatureStatuses from "@/constants/signatureStatuses";
import { PI, CRA, DM, SUB_PI, STUDY_COORDINATOR, HOME_HEALTH, CraUB } from "@/constants/authRoles";
import ParticipantInfo from "@/components/participants/participant-info-new";
import moment from "moment";
import swal from "sweetalert2";
import _ from "lodash";
import $ from "jquery";

const COLLECTION_IMAGE = {
  initials: {
    image: "/static/img/initials-icon.svg",
    buttonLabel: {
      markResponse: "Initial and continue",
      skipResponse: "Do not initial"
    }
  },
  signature: {
    image: "/static/img/signature-icon.svg",
    buttonLabel: {
      markResponse: "Sign and continue",
      skipResponse: "Do not sign"
    }
  },
  checkbox: {
    image: "/static/img/checkbox-icon.svg",
    buttonLabel: {
      markResponse: "Save and continue",
      skipResponse: "Do not place checkmark"
    }
  },
  text: {
    image: "/static/img/text-icon.svg",
    buttonLabel: {
      markResponse: "Save and continue",
      skipResponse: "Do not add text"
    }
  },
  name: {
    image: "/static/img/name-icon.svg",
    buttonLabel: {
      markResponse: "Add name and continue",
      skipResponse: "Do not add name"
    }
  }
};

export default {
  name: "document-information",
  components: { ParticipantInfo },
  props: {
    docId: String,
    docName: String,
    isPISigned: Boolean,
    isPISignRequired: Boolean,
    isParticipantSigned: Boolean,
    piSignedTime: Number,
    participantSignedTime: Number,
    docType: String,
    documentDetails: {
      type: Object
    },
    participantDetails: {
      type: Object
    },
    isParticipantSignatureProcessing: Boolean,
    isSiteSignatureProcessing: Boolean
  },
  data() {
    return {
      name: null,
      toEmail: null,
      signed: false,
      userCredentials: {
        username: "",
        password: ""
      },
      studyId: this.$route.params.studyId,
      participantId: this.$route.params.participantId,
      documentId: this.$route.params.documentId,
      inValidateDocumentId: this.$route.query && this.$route.query.inValidateDocumentId,
      signatureStatuses,
      charLimit: 60,
      areActionsAllowed: {
        document: false,
        telehealth: true,
        edcforms: false,
        approval: false,
        verifyBtn: false,
        send: true,
        resendSign: true,
        sign: true,
        print: true
      },
      documentDetailsComputed: this.documentDetails,
      showNavigationButtons: true,
      imageClicked: false,
      currentFieldFocusedIndex: 0,
      showConfiguredTags: false,
      showPreviousBtn: false,
      showSkipResponse: false,
      documentImageConstant: null,
      fixedDocumentImageWidth: 733,
      fixedDocumentImageHeight: 1,
      calculateDocPageLeft: 0,
      footerBtnLabel: { markResponse: "", skipResponse: "" },
      extraData: { showSignature: false, showInitial: false },
      disableMarkResponseBtn: false,
      textAreaCharCount: 0,
      textAreaCharLimit: 500,
      currentCount: 1,
      totalMainPage: 2,
      documentStatusInterval: null,
      documentStatusAPIInterval: null,
      toggleFeatures: []
    };
  },
  computed: {
    ...mapGetters(["getSignatureData", "user", "getBase64SignatureData", "getBase64InitialData"]),
    showDocDetails() {
      return this.$route.name === "document-details" || this.$route.name === "document-history";
    },
    canPiAccess() {
      this.$acl.change(`${PI}`);
      return this.$acl.check("userAccess");
    },
    canSubPiAccess() {
      this.$acl.change(`${SUB_PI}`);
      return this.$acl.check("userAccess");
    },
    canScAccess() {
      this.$acl.change(`${STUDY_COORDINATOR}`);
      return this.$acl.check("userAccess");
    },
    canCraAccess() {
      this.$acl.change(`${CRA}`);
      return this.$acl.check("userAccess");
    },
    canCraUBAccess() {
      this.$acl.change(`${CraUB}`);
      return this.$acl.check("userAccess");
    },
    canDmAccess() {
      this.$acl.change(`${DM}`);
      return this.$acl.check("userAccess");
    },
    canHomeHealthAccess() {
      this.$acl.change(`${HOME_HEALTH}`);
      return this.$acl.check("userAccess");
    },
    isESignRequired() {
      //check site country and study flag
      const isESignEnabled = _.find(this.toggleFeatures, {
        featureKey: "ElectronicSignature",
        enabled: true
      });

      if (isESignEnabled !== undefined && this.participantDetails.countryName.toLowerCase().indexOf("denmark") !== -1) {
        return true;
      } else {
        return false;
      }
    }
  },
  methods: {
    ...mapActions(["userLogin", "setSignatureAction", "setSignatureStatusAction", "setBase64SignatureAction", "setBase64InitialAction"]),
    signDocuemnt() {},
    showSignatureModal(ref) {
      if (ref === "signatureModal") {
        this.setSignatureStatusAction(this.signatureStatuses.SIGNATURE_INIT);
      }
      this.$refs[ref].show();
    },
    printDocument() {
      window.open(this.documentDetails.preSignedUrl, "_blank");
    },
    confirmSign() {
      if (this.name) {
        this.signed = true;
      }
    },
    closeModal(ref) {
      this.signed = false;
      this.$refs[ref].hide();
      this.userCredentials = {
        username: "",
        password: ""
      };
      this.name = null;
      this.setSignatureStatusAction(this.signatureStatuses.SIGNATURE_RESET);
      this.setSignatureAction(null);
    },
    requestPISignature() {
      const signObj = new FormData();
      signObj.append("piSign", this.getSignatureData);
      signObj.append("name", this.name);
      const dataObj = {
        studyId: this.studyId,
        participantId: this.participantId,
        docId: this.documentId,
        data: signObj
      };
      this.$api.requestPISignatureFact(dataObj).then(
        response => {
          this.$toastr.success(response.body && response.body.message ? response.body.message : "PI signature saved successfully");
          this.closeModal("signatureModal");
          this.setSignatureStatusAction(this.signatureStatuses.SIGNATURE_COMPLETED);
          this.$store.commit("SHOW_LOADER", true);
        },
        error => {
          this.$toastr.error(error.body && error.body.message ? error.body.message : "Error while getting participant.");
        }
      );
    },
    requestConfiguredDocumentPISignature(event) {
      const tagList = [];
      this.documentDetailsComputed.siteTeamConfigureList.forEach(e => {
        const tag = {
          id: e.id,
          sequence: e.sequence,
          fieldType: e.fieldType,
          fieldName: e.fieldType === "checkbox" ? e.fieldLabel : e.fieldName,
          responseText: e.fieldType == "name" && e.siteTeamResponse ? event.name : e.responseText,
          required: e.required,
          isSkip: !e.siteTeamResponse,
          responseById: e.docId,
          responseByRole: e.role,
          responseAtUtc: e.responseAtUtc,
          responseAtLocal: e.responseAtLocal,
          responseAtLocalOffset: e.responseAtLocalOffset,
          xCoordinate: e.xCoordinate,
          yCoordinate: e.yCoordinate,
          height: e.height,
          width: e.width,
          pageNumber: e.pageNumber
        };
        tagList.push(tag);
      });

      let requestObj = [
        {
          initialImageData: this.getBase64InitialData,
          signatureImageData: this.getBase64SignatureData,
          firstName: event.name,
          lastName: event.name,
          responseList: tagList
        }
      ];

      if (!!this.documentDetailsComputed.witnessConfigureList && this.documentDetailsComputed.witnessConfigureList.length > 0) {
        const witnessData = [];
        const witnessSignAndInitial = this.documentDetailsComputed.witnessConfigureList.filter(sign => {
          return sign.roleOrder == 1 || sign.roleOrder == 2;
        });

        witnessSignAndInitial.forEach(signs => {
          if (
            witnessData.filter(payloadData => {
              return payloadData.roleOrder == signs.roleOrder;
            }).length == 0
          ) {
            const responseListData = [];
            witnessSignAndInitial.map(sign => {
              if (sign.roleOrder === signs.roleOrder) {
                this.setTimestamp(sign);
                let isSkip = true;
                const witnessName = event.witnessData[sign.roleOrder] ? event.witnessData[sign.roleOrder].name.trim() : "";
                if (sign.fieldType == "signature") {
                  isSkip = !event.witnessData[sign.roleOrder].isSigned;
                }
                if (sign.fieldType == "initials") {
                  isSkip = !event.witnessData[sign.roleOrder].isInitialAdded;
                }
                if (sign.fieldType == "name" && witnessName !== "") {
                  isSkip = false;
                }

                responseListData.push({
                  id: sign.id,
                  sequence: sign.sequence,
                  fieldType: sign.fieldType,
                  fieldName: sign.fieldType === "checkbox" ? sign.fieldLabel : sign.fieldName,
                  required: sign.required,
                  isSkip,
                  responseById: sign.docId,
                  responseByRole: sign.role,
                  xCoordinate: sign.xCoordinate,
                  yCoordinate: sign.yCoordinate,
                  height: sign.height,
                  width: sign.width,
                  pageNumber: sign.pageNumber,
                  responseAtUtc: sign.responseAtUtc,
                  responseAtLocal: sign.responseAtLocal,
                  responseAtLocalOffset: sign.responseAtLocalOffset,
                  responseText: sign.fieldType == "name" ? witnessName : ""
                });
              }
            });

            const payload = {
              initialImageData: event.witnessData[signs.roleOrder].initial,
              signatureImageData: event.witnessData[signs.roleOrder].signature,
              firstName: event.witnessData[signs.roleOrder].name,
              lastName: event.witnessData[signs.roleOrder].name,
              roleOrder: signs.roleOrder,
              responseList: responseListData
            };

            if (!!event.witnessData[signs.roleOrder].initial || !!event.witnessData[signs.roleOrder].signature) {
              witnessData.push(payload);
            }
          }
        });

        requestObj = [...requestObj, ...witnessData];
      }

      const dataObj = {
        studyId: this.studyId,
        participantId: this.participantId,
        docId: this.documentId,
        requestObj
      };

      this.$api.requestConfiguredPISignatureFact(dataObj).then(
        response => {
          this.$toastr.success(response.body && response.body.message ? response.body.message : "PI signature saved successfully");
          this.setSignatureStatusAction(this.signatureStatuses.SIGNATURE_COMPLETED);
          this.setSignatureAction(null);
          this.setBase64SignatureAction(null);
          this.setBase64InitialAction(null);
          this.showConfiguredTags = false;
        },
        error => {
          this.$toastr.error(error.body && error.body.message ? error.body.message : "Error while getting participant.");
        }
      );
    },
    async loginUser() {
      if (!this.errors.any()) {
        try {
          await this.userLogin(this.userCredentials);
          this.requestPISignature();
        } catch (error) {
          this.$toastr.error(error.message);
        }
      } else if (!this.userCredentials.username && !this.userCredentials.password) {
        this.$toastr.error("Please enter your credentials.");
      } else if (!this.userCredentials.username) {
        this.$toastr.error("Please provide your email, it is required");
      } else if (!this.userCredentials.password) {
        this.$toastr.error("Please provide your password, it is required");
      }
    },
    plotFields() {
      if (this.documentDetails.configured) {
        const docPageWidth = $("#doc-page").width();
        this.calculateDocPageLeft = ((docPageWidth - this.fixedDocumentImageWidth) / docPageWidth) * 100;
      }
    },
    signatureCollected(event) {
      if (event && event.signCollected) {
        this.showConfiguredTags = event.signCollected;
      }
      this.$nextTick(() => {
        this.plotFields();
        this.selectField(0);
      });
    },
    nextField(response) {
      const index = this.currentFieldFocusedIndex;
      const header = $(".main-header");
      const parentContainer = $("#configured-document-container");
      $("html,body").animate({ scrollTop: parentContainer.offset().top - (header.height() + 12) }, 800);
      const container = $("#configured-document");

      /* start: Remove focus from previous field */
      const previousTo = $(".focused");
      if (previousTo) {
        previousTo.removeClass("focused");
      }
      this.resetIsFocusedFlag(index, false);
      /* end: Remove focus from previous field */

      if (response === "mark") {
        this.markAsResponse();
      } else {
        this.skipResponse();
        this.reInitializeUnmarkedField("checkbox", this.currentFieldFocusedIndex);
        this.reInitializeUnmarkedField("text", this.currentFieldFocusedIndex);
      }

      this.setTimestamp(this.documentDetailsComputed.siteTeamConfigureList[this.currentFieldFocusedIndex]);
      const startedWithIndex = this.currentFieldFocusedIndex;
      let fundOneNotMarked = false;
      const fields = this.documentDetailsComputed.siteTeamConfigureList;
      if (this.currentFieldFocusedIndex < this.documentDetailsComputed.siteTeamConfigureList.length - 1) {
        for (let i = this.currentFieldFocusedIndex; i < fields.length; i++) {
          if (fields[i].siteTeamResponse === null) {
            this.currentFieldFocusedIndex = i - 1;
            fundOneNotMarked = true;
            break;
          }
        }
        if (startedWithIndex === this.currentFieldFocusedIndex && !fundOneNotMarked) {
          // no unmarked fields after current field
          if (this.getFirstNotMarkedResponse() !== null) {
            this.currentFieldFocusedIndex = this.getFirstNotMarkedResponse() - 1;
          }
        }
      } else {
        if (this.getFirstNotMarkedResponse() !== null) {
          this.currentFieldFocusedIndex = this.getFirstNotMarkedResponse() - 1;
        }
      }
      if (this.getFirstNotMarkedResponse() === null) {
        this.$refs["participantInfoRef"].verifyWitnesses();
        return;
      }

      if (this.currentFieldFocusedIndex < this.documentDetailsComputed.siteTeamConfigureList.length - 1) {
        this.currentFieldFocusedIndex++;
      }
      this.showPreviousBtn = !!this.currentFieldFocusedIndex; // show previous button when index is greater than zero
      this.showSkipResponse = !this.documentDetailsComputed.siteTeamConfigureList[this.currentFieldFocusedIndex].required;
      this.setButtons(this.documentDetailsComputed.siteTeamConfigureList[this.currentFieldFocusedIndex]);
      this.disableMarkButton(this.documentDetailsComputed.siteTeamConfigureList[this.currentFieldFocusedIndex]);

      const scrollTo = $("#configured-field-" + this.currentFieldFocusedIndex);
      scrollTo.addClass("focused");
      this.resetIsFocusedFlag(this.currentFieldFocusedIndex, true);
      // animate the scrolling:
      const containerHeight = container.height() / 2;
      const scrollHeight = scrollTo.outerHeight() / 2;
      container.animate(
        {
          scrollTop: scrollTo.offset().top - container.offset().top + container.scrollTop() - (containerHeight - scrollHeight)
        },
        900
      );
    },
    previousField() {
      const index = this.currentFieldFocusedIndex;
      const header = $(".main-header");
      const parentContainer = $("#configured-document-container");
      $("html,body").animate({ scrollTop: parentContainer.offset().top - (header.height() + 12) }, 800);
      const container = $("#configured-document");
      const nextTo = $(".focused");
      if (nextTo) {
        nextTo.removeClass("focused");
      }
      this.reInitializeUnmarkedField("checkbox", this.currentFieldFocusedIndex);
      this.reInitializeUnmarkedField("text", this.currentFieldFocusedIndex);

      this.resetIsFocusedFlag(index, false);

      if (index > 0) {
        this.currentFieldFocusedIndex--;
      }
      this.showPreviousBtn = !!this.currentFieldFocusedIndex; // show previous button when index is greater than zero
      this.showSkipResponse = !this.documentDetailsComputed.siteTeamConfigureList[this.currentFieldFocusedIndex].required;
      const scrollTo = $("#configured-field-" + this.currentFieldFocusedIndex);
      scrollTo.addClass("focused");
      this.resetIsFocusedFlag(this.currentFieldFocusedIndex, true);

      this.setButtons(this.documentDetailsComputed.siteTeamConfigureList[this.currentFieldFocusedIndex]);
      this.disableMarkButton(this.documentDetailsComputed.siteTeamConfigureList[this.currentFieldFocusedIndex]);
      // animate the scrolling:
      const containerHeight = container.height() / 2;
      const scrollHeight = scrollTo.outerHeight() / 2;
      container.animate(
        {
          scrollTop: scrollTo.offset().top - container.offset().top + container.scrollTop() - (containerHeight - scrollHeight)
        },
        900
      );
    },
    manageFields() {
      const separatorHeight = 13;
      if (this.fixedDocumentImageWidth > this.documentDetailsComputed.documentImages[0].width) {
        this.fixedDocumentImageWidth = this.documentDetailsComputed.documentImages[0].width;
      }
      const aspectRatio = this.documentDetailsComputed.documentImages[0]["width"] / this.documentDetailsComputed.documentImages[0]["height"];
      this.fixedDocumentImageHeight = this.fixedDocumentImageWidth / aspectRatio;
      const sortedArray = this.documentDetailsComputed.siteTeamConfigureList;
      for (let i = 0; i < sortedArray.length; i++) {
        this.addExtraKeys(sortedArray[i]);
        this.calculateTagCoordinates(sortedArray[i], separatorHeight);
        if (sortedArray[i].fieldType === "signature") {
          // Managed sign & initial on modal popup
          this.extraData.showSignature = true;
        } else if (sortedArray[i].fieldType === "initials") {
          this.extraData.showInitial = true;
        }
      }
      this.$forceUpdate();
    },
    calculateTagCoordinates(obj) {
      const xPerUnitWidth = obj.xCoordinate / this.fixedDocumentImageWidth;
      const yPerUnitHeight = obj.yCoordinate / this.fixedDocumentImageHeight;
      obj["plottingPts"] = {
        x: xPerUnitWidth * this.fixedDocumentImageWidth,
        y: yPerUnitHeight * this.fixedDocumentImageHeight
      };
    },
    addExtraKeys(obj) {
      obj["image"] = COLLECTION_IMAGE[obj["fieldType"]]["image"];
      obj["siteTeamResponse"] = null;
      obj["plottingPts"] = { x: null, y: null };
      obj["responseAtUtc"] = null;
      obj["responseAtLocal"] = null;
      obj["responseAtLocalOffset"] = null;
      obj["isFocused"] = false;
      obj["responseText"] = null;
      if (obj.fieldType === "checkbox") {
        obj["responseText"] = "false";
      }
    },
    selectField(index) {
      this.manageCheckmarkResponse(index);
      if (this.currentFieldFocusedIndex !== index) {
        this.reInitializeUnmarkedField("checkbox", this.currentFieldFocusedIndex);
        this.reInitializeUnmarkedField("text", this.currentFieldFocusedIndex);
      }

      const header = $(".main-header");
      const parentContainer = $("#configured-document-container");
      $("html,body").animate({ scrollTop: parentContainer.offset().top - (header.height() + 12) }, 800);
      const container = $("#configured-document");
      const previousTo = $(".focused");
      if (previousTo) {
        previousTo.removeClass("focused");
      }
      this.resetIsFocusedFlag(this.currentFieldFocusedIndex, false);

      this.showPreviousBtn = !!index; // show previous button when index is greater than zero
      this.showSkipResponse = !this.documentDetailsComputed.siteTeamConfigureList[index].required;
      this.setButtons(this.documentDetailsComputed.siteTeamConfigureList[index]);
      this.disableMarkButton(this.documentDetailsComputed.siteTeamConfigureList[index]);
      const scrollTo = $("#configured-field-" + index);
      scrollTo.addClass("focused");
      scrollTo.parent().removeClass("green-checkmark");
      this.resetIsFocusedFlag(index, true);

      const containerHeight = container.height() / 2;
      const scrollHeight = scrollTo.outerHeight() / 2;
      // animate the scrolling:
      container.animate(
        {
          scrollTop: scrollTo.offset().top - container.offset().top + container.scrollTop() - (containerHeight - scrollHeight)
        },
        900
      );
      this.currentFieldFocusedIndex = index;
    },
    addCheckmark() {
      const inputBox = $("#configured-field-" + this.currentFieldFocusedIndex);
      inputBox.parent().addClass("green-checkmark");
    },
    markAsResponse() {
      const obj = this.documentDetailsComputed.siteTeamConfigureList[this.currentFieldFocusedIndex];
      obj["siteTeamResponse"] = true;
      this.addCheckmark();
    },
    skipResponse() {
      const obj = this.documentDetailsComputed.siteTeamConfigureList[this.currentFieldFocusedIndex];
      obj["siteTeamResponse"] = false;
    },
    getFirstNotMarkedResponse() {
      const fields = this.documentDetailsComputed.siteTeamConfigureList;
      for (let i = 0; i < fields.length; i++) {
        if (fields[i].siteTeamResponse === null) {
          return i;
        }
      }
      return null;
    },
    cancelConfiguredDocumentSigning() {
      swal({
        title: "Are you sure you want to cancel?<br> Any provided responses won't be saved.",
        type: "warning",
        showCancelButton: true,
        confirmButtonColor: "#b8c7ce",
        cancelButtonColor: "rgb(52, 83, 125)",
        confirmButtonText: "Cancel",
        cancelButtonText: "Yes"
      }).then(({ dismiss }) => {
        if (dismiss === "cancel") {
          this.initialize();
        } else {
        }
      });
    },
    initialize() {
      this.showConfiguredTags = false;
      this.setSignatureAction(null);
      this.setSignatureStatusAction(this.signatureStatuses.SIGNATURE_RESET);
      this.$refs["participantInfoRef"].initialize();
      this.documentDetailsComputed.siteTeamConfigureList.forEach(data => {
        data["siteTeamResponse"] = null;
        data["responseAtUtc"] = null;
        data["responseAtLocal"] = null;
        data["responseAtLocalOffset"] = null;
        data["isFocused"] = false;
        data["responseText"] = null;
        if (data.fieldType === "checkbox") {
          data["responseText"] = "false";
        }
      });
    },
    setButtons(obj) {
      const fieldType = obj.fieldType;
      for (const prop in COLLECTION_IMAGE[fieldType]["buttonLabel"]) {
        this.footerBtnLabel[prop] = COLLECTION_IMAGE[fieldType]["buttonLabel"][prop];
      }
    },
    restoreFocus() {
      this.selectField(this.currentFieldFocusedIndex);
    },
    setTimestamp(obj) {
      const now = new Date();
      obj["responseAtUtc"] = moment.utc(now).format("YYYY-MM-DD HH:mm:ss");
      obj["responseAtLocal"] = moment(now).format("YYYY-MM-DDTHH:mm:ss.SSSZZ");
      obj["responseAtLocalOffset"] = moment(now).format("Z");
    },
    checkTextResponse(obj) {
      if (obj.fieldType == "text") {
        this.textAreaCharCount = obj.responseText && obj.responseText.length ? obj.responseText.length : 0;
        this.disableMarkResponseBtn = false;
        if (obj.required && !(obj.responseText && obj.responseText.trim())) {
          // required and empty: clean the response & disable button
          this.disableMarkResponseBtn = true;
          obj.siteTeamResponse = null;
        } else if (!obj.required && !(obj.responseText && obj.responseText.trim())) {
          // optional and empty: disable button
          this.disableMarkResponseBtn = true;
        }
      }
    },
    checkCheckmarkResponse(obj) {
      if (obj.fieldType == "checkbox") {
        this.disableMarkResponseBtn = false;
        if (obj.required && obj.responseText !== "true") {
          // required and empty: clean the response & disable button
          this.disableMarkResponseBtn = true;
          obj.siteTeamResponse = null;
        } else if (!obj.required && obj.responseText !== "true") {
          // optional and empty: disable button
          this.disableMarkResponseBtn = true;
        }
      }
    },
    disableMarkButton(obj) {
      this.disableMarkResponseBtn = false;
      this.checkCheckmarkResponse(obj);
      this.checkTextResponse(obj);
    },
    resetIsFocusedFlag(index, value) {
      this.documentDetailsComputed.siteTeamConfigureList[index].isFocused = value;
    },
    manageCheckmarkResponse(index) {
      if (this.documentDetailsComputed.siteTeamConfigureList[index].isFocused) {
        this.documentDetailsComputed.siteTeamConfigureList[index].responseText = this.documentDetailsComputed.siteTeamConfigureList[index].responseText === "true" ? "false" : "true";
      }
    },
    reInitializeUnmarkedField(type, index) {
      const tag = this.documentDetailsComputed.siteTeamConfigureList[index];
      if (type === "checkbox") {
        if (tag.fieldType === type && !tag.siteTeamResponse) {
          tag.responseText = "false";
        }
      } else if (type === "text") {
        if (tag.fieldType === type && !tag.siteTeamResponse) {
          tag.responseText = null;
        }
      }
    },
    callSinglePdfImage() {
      const docObj = {
        studyId: this.studyId,
        participantId: this.participantId,
        documentId: this.documentId,
        isInvalidate: !!this.inValidateDocumentId
      };
      if (this.inValidateDocumentId) {
        docObj.inValidateDocumentId = this.inValidateDocumentId;
      }
      docObj.page = this.currentCount;
      this.totalMainPage = parseInt(this.documentDetailsComputed.defaultPageCount);
      if (this.currentCount === 1) {
        this.$store.commit("SHOW_LOADER", true);
      }
      if (this.currentCount < this.totalMainPage) {
        docObj.page = parseInt(docObj.page);
        this.$api.getPageWiseDocumentDetailsFact(docObj).then(
          response => {
            this.$store.commit("SHOW_LOADER", false);
            this.documentDetailsComputed.documentImages = this.documentDetailsComputed.documentImages.concat(response.body.documentImages);
            this.currentCount = this.currentCount + 1;
            this.callSinglePdfImage();
          },
          error => {
            this.$store.commit("SHOW_LOADER", false);
            this.$toastr.error(error.body && error.body.message ? error.body.message : "Error while getting configured document details");
          }
        );
      }
    },
    scrollImage(e) {
      if (e.target.offsetHeight + e.target.scrollTop + 5 >= e.target.scrollHeight) {
        if (this.currentCount != this.totalMainPage) {
          this.$store.commit("SHOW_LOADER", true);
        }
      }
    },
    checkDocStatus() {
      if (!this.isParticipantSignatureProcessing && !this.isSiteSignatureProcessing) {
        clearInterval(this.documentStatusAPIInterval);
        clearInterval(this.documentStatusInterval);
        this.callSinglePdfImage();
      }
    },
    pIDocSignatureStatus() {
      this.$emit("pIDocSignatureStatus");
    },
    getToggledFeatures() {
      this.$api.getToggleFeaturesFact(this.studyId).then(
        response => {
          if (response.body) {
            this.toggleFeatures = response.body.studyFeatures;
          }
        },
        error => {
          this.$toastr.error(error.body && error.body.message ? error.body.message : "Error while getting study features.");
        }
      );
    }
  },
  watch: {
    documentDetails: {
      immediate: true,
      handler(data) {
        if (data) {
          if (this.documentDetails && this.documentDetails.configured) {
            this.documentDetailsComputed = { ...this.documentDetails };
            this.manageFields();
          } else if (this.documentDetails && this.documentDetails.consentId) {
            this.extraData = { showSignature: true, showInitial: false };
          }
        }
      }
    },
    getSignatureStatus: {
      handler(val) {
        if (val === this.signatureStatuses.SIGNATURE_INIT || val === this.signatureStatuses.SIGNATURE_RESET) {
          window.setTimeout(() => {
            this.resizeCanvas();
          }, 100);
        }
      }
    }
  },
  created() {
    this.checkDocStatus();
    this.getToggledFeatures();
  },
  beforeDestroy() {
    clearInterval(this.documentStatusInterval);
    clearInterval(this.documentStatusAPIInterval);
  },
  mounted() {
    this.documentStatusInterval = window.setInterval(() => {
      if (this.isParticipantSignatureProcessing || this.isSiteSignatureProcessing) {
        this.$store.commit("SHOW_LOADER", true);
      } else {
        this.$store.commit("SHOW_LOADER", false);
      }
    }, 2000);
    if (this.isParticipantSignatureProcessing || this.isSiteSignatureProcessing) {
      this.documentStatusAPIInterval = window.setInterval(() => {
        this.checkDocStatus();
      }, 6000);
    }
  }
};
</script>

<style scoped lang="scss">
.buttons-wrap {
  height: 40px;
  text-align: center;
  line-height: 40px;
  margin-top: 10px;
  margin-bottom: 10px;
  button {
    border-radius: 2px;
    border: solid 0.5px #e4e8eb;
    font-weight: bold;
    width: 160px;
    padding: 8px;
    letter-spacing: 0.2px;
    font-size: 12px;
  }
  button:nth-child(n) {
    margin-right: 10px;
  }
  button:last-child {
    margin-right: 0px;
  }
  .btn-default {
    background-color: #ffffff;
    color: #4c8ce4;
    transition-duration: 0.6s;
    &.active {
      background-color: #4c8ce4;
      color: #ffffff;
      transition-duration: 0.6s;
    }
  }
  .btn-default:focus {
    box-shadow: none;
  }
  .btn-primary {
    background-color: #4c8ce4;
    color: #ffffff;
  }
}
.doc-details {
  clear: both;
  /deep/ .modal-content {
    min-width: 425px;
  }
  .btn.disabled,
  .btn:disabled {
    pointer-events: none;
  }
}
#confirmSendModal {
  .modal-heading {
    font-size: 14px;
  }
  .custom-btn {
    width: 100%;
  }
}
.document-detail-wrap {
  padding: 15px 0px;
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.06);
  background-color: #fff;
  margin-top: 20px;
  .heading {
    letter-spacing: 0.5px;
    color: #a8a8a8;
  }
}
.font-1rem {
  font-size: 1rem;
}
.pi-info-bars {
  .heading {
    color: #4a4a4a;
    font-weight: 600;
    letter-spacing: 0.2px;
  }
  .pi-info-bar {
    box-shadow: 0 3px 2px 0 rgba(0, 0, 0, 0.06);
    padding: 15px 0px;
    &.resize-text {
      font-size: 10px;
    }
    .custom-max-width {
      &.email {
        max-width: 12%;
      }
      &.gender {
        max-width: 13.66666%;
      }
    }
  }
}

.green-checkmark::before {
  content: url("/static/img/checkmark-3.svg");
  position: absolute;
  margin-left: 30px;
  margin-top: -7px;
  animation: green-check 3s ease-in-out;
  opacity: 0;
  z-index: 100;
}

@keyframes green-check {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  30% {
    transform: scale(1.5);
    opacity: 1;
  }
  60% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0;
  }
}

.plot-collection {
  position: absolute;

  & .field-wrapper {
    position: absolute;

    &.focused {
      border: 3px #4db6ac solid;
      padding-top: 7.5px;
    }
    & .sign-wrapper,
    .checkbox-wrapper,
    .txtarea-wrapper {
      position: absolute;
      width: max-content;
      & .fieldValue {
        background-color: transparent;
        width: 42px;
        height: 43px;
        border-radius: 3px;
        border: 1px #4c8ce4 solid;
        text-align: center;
        vertical-align: middle;
        padding-top: 9.5px;
        align-content: center;
        border-radius: 6px;
        margin-right: 0;
        display: inline-block;
        z-index: 99;
        position: relative;
        float: left;
        &.focused {
          border: 3px #4db6ac solid;
          padding-top: 7.5px;
        }
        & .image-cropper {
          width: 26px;
          height: 26px;
          padding: 3.5px;
          border-radius: 50%;
          border: 1px #c4c4c4 solid;
          text-align: center;
          margin: auto;
          margin: 4px;
          background-color: #f9f5d6;
          & .field-image {
            display: inline;
            height: 16px;
            width: 18px;
            position: relative;
            top: -2px;
          }
          & .checkbox-img {
            height: 13px;
            width: 12px;
            margin: 0 3px;
          }
          & .txtarea-img {
            height: 21px;
            width: 19px;
            margin: 0px;
            margin-bottom: 2px;
          }
        }
      }
      & .txtarea {
        position: relative;
        border: 3px #4db6ac solid;
        -webkit-border-radius: 6px;
        -moz-border-radius: 6px;
        border-radius: 6px;
        resize: none;
        &:focus {
          box-shadow: 0px 0px 4px 0px #4db6ac;
        }
      }
      & .checkbox-label {
        position: relative;
        float: right;
        margin: 10px 0 10px 8px;
        font-family: "RobotoRegular";
        font-size: 16px;
        font-weight: normal;
        font-style: normal;
        color: #000000;
      }
    }
  }
}
.doc-outer-page {
  width: auto;
  height: auto;
  position: relative !important;
  margin: 40px auto;
  & .doc-page {
    height: auto;
    position: relative !important;
    & .separator {
      width: 100%;
      background-color: #c4c4c4;
      height: 13px;
      position: relative;
    }
  }
}
.configured-document-section {
  margin: 16px;
  & .configured-document-sub-section {
    overflow-y: scroll;
    height: 500px;
    position: relative;
    background-color: #525659;
    border: 2px #525659 solid;
    &.bottom-border {
      border-bottom: 0px;
    }
    &::-webkit-scrollbar {
      width: 14px;
      background-color: #fff;
    }
    &::-webkit-scrollbar-thumb {
      background-color: #c4c4c4;
      border-radius: 10px;
      border: 3.5px solid transparent;
      background-clip: content-box;
      width: 6px;
      height: 63px;
    }
    &::-webkit-scrollbar-track {
      background-color: #fff;
    }
  }
}
.save-btn {
  font-family: RobotoRegular;
  font-size: 14px;
  font-weight: bold;
  float: right;
  padding: 6px 14px;
  color: #ffffff;
  border-radius: 4px;
  cursor: pointer;
  border: solid 1px #508ee1 !important;
  background-color: #508ee1 !important;
  &:active {
    border: solid 1px #508ee1 !important;
    outline: none !important;
  }
  &:focus {
    border: solid 1px #508ee1 !important;
    outline: none !important;
  }
  &:hover:not([disabled]) {
    color: #fff;
    background-color: #3b81de !important;
    border-color: #0062cc !important;
  }
  &:disabled {
    background-color: #7babea !important;
    background-color: #7cabe9 !important;
    cursor: not-allowed;
  }
}
.previous-btn {
  color: #4c8ce4;
  cursor: pointer;
  &:active {
    color: #4ba6e7;
  }
  &:focus {
    color: #4ba6e7;
  }
  &:hover {
    color: #4ba6e7;
  }
  & .previous-text {
    width: 90px;
    height: 24px;
    font-family: RobotoRegular;
    font-size: 14px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.71;
    letter-spacing: normal;
    top: 6px;
    position: absolute;
  }
}
.cancel-btn {
  font-family: RobotoRegular;
  font-size: 14px;
  font-weight: normal;
  color: #ffffff !important;
  border-radius: 4px;
  margin-right: 14px;
  cursor: pointer;
  background-color: #b0bac9;
  border: solid 1px #508ee1 !important;
  padding: 6px 26px;
  &:active {
    border: solid 1px #508ee1 !important;
    outline: none !important;
  }
  &:focus {
    border: solid 1px #508ee1 !important;
    outline: none !important;
  }
  &:hover {
    background-color: #a4b0c1 !important;
    border: solid 1px #508ee1 !important;
    outline: none !important;
  }
}
.skip-btn {
  font-family: RobotoRegular;
  font-size: 14px;
  font-weight: normal;
  color: #4c8ce4;
  border-radius: 4px;
  margin-right: 14px;
  border: solid 1px #4c8ce4;
  background-color: #ffffff;
  cursor: pointer;
  padding: 6px 14px;
  &:active {
    border: solid 1px #508ee1 !important;
    outline: none !important;
  }
  &:focus {
    border: solid 1px #508ee1 !important;
    outline: none !important;
  }
  &:hover {
    color: #4ba6e7;
    border: solid 1px #508ee1 !important;
    outline: none !important;
  }
}
.document-footer {
  border: 2.3px #525659 solid;
  border-top: 0px;
  & .btn-container {
    padding-top: 18px;
    padding-bottom: 21px;
    margin: 0px 125px !important;
  }
}
.document-name {
  word-wrap: break-word !important;
}
</style>
